import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from 'firebase/auth'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import { HiLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi'
import MiniFooter from '../../components/MiniFooter'
import { actionCodeSettings } from '../../constants/actionCodeSettings'
import * as ROUTES from '../../constants/routes'
import { auth, firestore } from '../../lib/firebaseConfig'
import { doesEmailExist } from '../../lib/firebaseServices'

interface SignupProps {}

const Signup: FC<SignupProps> = ({}) => {
  const router = useRouter()
  const initialState = {
    name: '',
    email: '',
    password: '',
  }

  const [error, setError] = useState('')
  const [input, setInput] = useState(initialState)
  const [showPassword, setShowPassword] = useState(false)

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setInput((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const emailExists = await doesEmailExist(input.email)
    if (emailExists) setError('E-Mail gibt es bereits.')
    if (!emailExists) {
      try {
        /**
         * Create user account
         */
        const cred = await createUserWithEmailAndPassword(auth, input.email, input.password)

        await updateProfile(cred.user, {
          displayName: input.name,
        })

        await setDoc(doc(firestore, 'users', cred.user.uid), {
          uid: cred.user.uid,
          name: input.name,
          email: input.email.toLowerCase(),
          authProvider: 'emailpw',
          dateCreated: serverTimestamp(),
          amountActiveAppointments: 0,
          amountInactiveAppointments: 0,
        })

        /**
         * E-Mail verification
         */

        await sendEmailVerification(cred.user, actionCodeSettings)
        localStorage.setItem('emailForSignIn', input.email)
        router.push('/myaccount/account-created')
      } catch (error) {
        setInput(initialState)
        setError('Error')
      }
    }
  }

  return (
    <>
      <Head>
        <title>Account erstellen - Praxis am Bulkamp</title>
        <meta name='description' content='Zahnärzte Praxis am Bulkamp' />
        <link rel='icon' href='/logo-without-text.png' />
      </Head>
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-around pb-6 pt-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div>
            <div className='relative mx-auto h-12 w-auto'>
              <Image
                quality={100}
                src='/logo-without-text.png'
                layout='fill'
                objectFit='contain'
                alt='Praxis Am Bulkamp Logo'
                className='pointer-events-none select-none'
              />
            </div>
            <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
              Erstellen&nbsp;Sie Ihren&nbsp;Account
            </h2>
            <p className='mt-2 text-center text-sm text-gray-600'>
              Sie haben bereits einen Account?{' '}
              <Link href={ROUTES.LOGIN}>
                <a className='font-medium text-indigo-600 hover:text-indigo-500'>Login</a>
              </Link>
            </p>
          </div>
          <form className='mt-8 space-y-6' onSubmit={handleSignUp} method='POST'>
            <input type='hidden' name='remember' defaultValue='true' />
            <div className='rounded-md shadow-sm -space-y-px'>
              {error && <p>{error}</p>}
              <div>
                <label htmlFor='email-address' className='sr-only'>
                  Vollständiger Name
                </label>
                <input
                  value={input.name}
                  onChange={handleInput}
                  name='name'
                  type='text'
                  autoComplete='name'
                  required
                  className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                  placeholder='Ihr vollständiger Name'
                />
              </div>
              <div>
                <label htmlFor='email-address' className='sr-only'>
                  Email Adresse
                </label>
                <input
                  value={input.email}
                  onChange={handleInput}
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                  placeholder='Email Adresse'
                />
              </div>
              <div className='relative'>
                <label htmlFor='password' className='sr-only'>
                  Passwort
                </label>
                <input
                  value={input.password}
                  onChange={handleInput}
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  required
                  className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                  placeholder='Passwort'
                />
                <span className='absolute z-30 cursor-pointer right-0 inset-y-0 flex items-center pr-3'>
                  {showPassword ? (
                    <HiOutlineEyeOff
                      className='h-5 w-5 text-gray-400 transition hover:text-gray-500'
                      aria-hidden='true'
                      onClick={() => setShowPassword((prev) => !prev)}
                    />
                  ) : (
                    <HiOutlineEye
                      className='h-5 w-5 text-gray-400 transition hover:text-gray-500'
                      aria-hidden='true'
                      onClick={() => setShowPassword((prev) => !prev)}
                    />
                  )}
                </span>
              </div>
            </div>

            <div>
              <button
                type='submit'
                className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'>
                <span className='absolute left-0 inset-y-0 flex items-center pl-3'>
                  <HiLockClosed
                    className='h-5 w-5 text-indigo-500 group-hover:text-indigo-400'
                    aria-hidden='true'
                  />
                </span>
                Account erstellen
              </button>
            </div>
          </form>
        </div>
        <MiniFooter />
      </div>
    </>
  )
}

export default Signup
